import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useNavigate } from "react-router-dom";

import { FIND_PERSON_COMPANY_CREDITS } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { StepInfo } from "@/components/StepInfo/StepInfo";
import { Toggle } from "@/components/Toggle/Toggle";
import { ApolloType } from "@/enum/apollo.enum";
import { useUpdateApolloMutation } from "@/pages/Private/redux/apollo/apollo.api";
import { describeSaveFailure } from "@/utils/describeSaveFailure";
import { showGlobalToast } from "@/utils/showGlobalToast";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { ListBuilderLayout } from "../../../components/ListBuilderLayout";
import { List } from "../../../schema/list";
import { getAvailableSteps, getDoneSteps, isDetailsNeeded } from "../../../utils/create-list-steps";
import { EnrichmentFilterConfig } from "../ListEditDetails";
import { OverrideRule } from "../types/override-rules";
import { DefineIcps } from "./DefineIcps";
import { OverrideRules } from "./OverrideRules";
import { Stepper } from "./Stepper";
import { hasSearchProfile, trackerFindPersonConfig } from "./trackerFindPersonConfig";
import { placementLookupPrices } from "./trackerFindPersonPrice";

/**
 * The Find person step of a tracker list (PREC-4399): a switch that lets a P1-P3 signal for a
 * company where the tracker knows nobody start a search for one person there, and the search
 * profiles it uses. Off by default; nothing is searched or charged while it is off. The switch and
 * the profiles are saved together in the list's source apollo config.
 */
export const TrackerFindPersonStep: FunctionComponent<{ list: List }> = ({ list }) => {
	const { t } = useTranslation();
	const tf = (key: string, options?: Record<string, unknown>) =>
		t(`defineIcps.trackerFindPerson.${key}`, options);
	const navigate = useNavigate();
	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);
	const [updateSearch, { isLoading }] = useUpdateApolloMutation();

	const apollo = list.apollos?.find((candidate) => candidate.type === ApolloType.SOURCE);
	const savedFilters = apollo?.config?.enrichmentFilterConfig as EnrichmentFilterConfig | undefined;

	const [enabled, setEnabled] = useState(apollo?.config?.findPersonOnSignal === true);
	const [titleFilters, setTitleFilters] = useState<EnrichmentFilterConfig | undefined>(
		savedFilters
	);
	const [overrideRules, setOverrideRules] = useState<OverrideRule[]>(
		savedFilters?.overrideRules ?? []
	);
	const [showErrors, setShowErrors] = useState(false);

	const hasErrors = Object.values(titleFilters ?? {}).some(
		(filter) => !Array.isArray(filter) && filter?.error
	);
	const missingProfile = enabled && !hasSearchProfile(titleFilters);
	// The lookups the person placed on the row gets, as the list asks for them (PREC-4570)
	const lookupPrices = placementLookupPrices(list);

	// No listId: with one, the apollo update also rewrites the list's AI columns from this config,
	// which names none, and deletes them. refreshListId only refreshes this list's cached data.
	const save = async (source: NonNullable<typeof apollo>) => {
		await updateSearch({
			id: source.id,
			name: list.name || "",
			type: ApolloType.SOURCE,
			config: trackerFindPersonConfig(source.config, enabled, titleFilters, overrideRules),
			refreshListId: list.id,
			updateType: "entityFilter",
		}).unwrap();
	};

	const submit = async (direction: "next" | "previous") => {
		if (direction === "next" && (missingProfile || hasErrors)) {
			setShowErrors(true);
			showGlobalToast(missingProfile ? tf("needsProfile") : t("list.detailsStep.incomplete"));

			return;
		}

		if (!apollo) {
			showGlobalToast(tf("noSource"));

			return;
		}

		try {
			await save(apollo);
		} catch (error) {
			showGlobalToast(describeSaveFailure(error, t("list.detailsStep.saveFailed")));

			return;
		}

		if (direction === "previous") {
			navigate(`/app/lists/create/signals/${list.id}`);
		} else {
			navigate(`/app/lists/create/${isSequencerActive ? "copywriting" : "settings"}/${list.id}`);
		}
	};

	return (
		<ListBuilderLayout
			bodyClassName="w-full p-4 pb-32"
			sourceTypeLabel={t(`list.${list.sourceType}`)}
		>
			<Stepper
				availableSteps={getAvailableSteps(isDetailsNeeded(list), list, isSequencerActive)}
				currentStep="details"
				doneSteps={getDoneSteps(list)}
				listId={list.id}
			/>
			<StepInfo description={<p>{tf("stepDescription")}</p>} title={tf("stepTitle")} />
			<div className="w-full">
				<div className="bg-white border border-border-light rounded-lg p-5 mb-5 flex items-start gap-4">
					<Toggle
						isChecked={enabled}
						testId="tracker-find-person-switch"
						onChange={() => setEnabled((value) => !value)}
					/>
					<div className="flex-1">
						<div className="text-sm font-semibold text-text">{tf("switchLabel")}</div>
						<div className="text-xs text-gray-700 mt-1">
							{enabled ? tf("switchHelp") : tf("offHelp")}
						</div>
						<span
							className="inline-block mt-2 text-xs rounded px-2 py-0.5 bg-brand-white text-brand-light"
							data-testid="tracker-find-person-price"
						>
							{tf("price", { credits: FIND_PERSON_COMPANY_CREDITS })}
						</span>
						{lookupPrices.length > 0 && (
							<ul className="mt-1 text-xs text-gray-700" data-testid="tracker-find-person-lookups">
								<li>{tf("priceLookupIntro")}</li>
								{lookupPrices.map(({ key, count }) => (
									<li key={key}>{tf(key, { count })}</li>
								))}
							</ul>
						)}
					</div>
				</div>
				{enabled && (
					<>
						<DefineIcps
							hidePeoplePerCompany
							hasErrors={showErrors && (hasErrors || missingProfile)}
							list={list}
							maxPerCompany={1}
							setMaxPerCompany={() => undefined}
							setTitleFilters={setTitleFilters}
							titleFilters={titleFilters}
						/>
						<div className="text-xs text-gray-700 mb-5">{tf("fixed")}</div>
						<OverrideRules rules={overrideRules} onChange={setOverrideRules} />
					</>
				)}
			</div>
			<div className="absolute mt-14 h-16 flex flex-row justify-between items-center border-t border-t-border px-8 py-[14px] rounded-t-16 bottom-0 right-0 z-[999] bg-white w-full">
				<div></div>
				<div className="flex flex-row self-left items-center gap-x-4">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						isLoading={isLoading}
						title={t("basics.previous")}
						onClick={() => submit("previous")}
					/>
					<Button isLoading={isLoading} title={t("basics.next")} onClick={() => submit("next")} />
				</div>
			</div>
		</ListBuilderLayout>
	);
};
